import { useId } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import styles from './Field.module.css';

interface FieldShellProps {
  readonly label: string;
  readonly required?: boolean | undefined;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
  readonly children: (ids: { id: string; describedBy: string | undefined }) => ReactNode;
}

/**
 * Label + control + message wrapper.
 *
 * The label is a real `<label for>` and any hint or error is wired through
 * `aria-describedby`, so screen readers announce the field, its help text and
 * its error together instead of reading a bare input.
 */
function FieldShell({ label, required, hint, error, children }: FieldShellProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {required ? (
          <span className={styles.required} aria-hidden="true">
            *
          </span>
        ) : null}
      </label>

      {children({ id, describedBy })}

      {hint ? (
        <span className={styles.hint} id={hintId}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span className={styles.error} id={errorId}>
          {error}
        </span>
      ) : null}
    </div>
  );
}

type Shared = Pick<FieldShellProps, 'label' | 'required' | 'hint' | 'error'>;

export type TextFieldProps = Shared &
  Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'aria-describedby'>;

export function TextField({ label, required, hint, error, ...input }: TextFieldProps) {
  return (
    <FieldShell label={label} required={required} hint={hint} error={error}>
      {({ id, describedBy }) => (
        <input
          {...input}
          id={id}
          className={`${styles.control} ${error ? styles.invalid : ''}`}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          required={required}
        />
      )}
    </FieldShell>
  );
}

export type TextAreaFieldProps = Shared &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id' | 'aria-describedby'>;

export function TextAreaField({ label, required, hint, error, ...textarea }: TextAreaFieldProps) {
  return (
    <FieldShell label={label} required={required} hint={hint} error={error}>
      {({ id, describedBy }) => (
        <textarea
          {...textarea}
          id={id}
          className={`${styles.control} ${error ? styles.invalid : ''}`}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          required={required}
        />
      )}
    </FieldShell>
  );
}

export type SelectFieldProps = Shared &
  Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'aria-describedby'> & {
    readonly children: ReactNode;
  };

export function SelectField({
  label,
  required,
  hint,
  error,
  children,
  ...select
}: SelectFieldProps) {
  return (
    <FieldShell label={label} required={required} hint={hint} error={error}>
      {({ id, describedBy }) => (
        <select
          {...select}
          id={id}
          className={`${styles.control} ${styles.select} ${error ? styles.invalid : ''}`}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          required={required}
        >
          {children}
        </select>
      )}
    </FieldShell>
  );
}
